import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';
import { Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { MY_SOCIAL_CONNECTIONS } from '@/queries/queries';
import { isDirectPostConnection } from '@/lib/publishing/resolvePublishEligibility';
import type { PublishConnection, PublishDialogTarget } from '@/lib/publishing/types';
import { SendToTikTokDialog } from '@/components/publishing/SendToTikTokDialog';
import { PublishToTikTokDialog } from '@/components/publishing/PublishToTikTokDialog';
import type { PublishWhen } from '@/components/publishing/PublishWhenControl';

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  clipId: string;
  clipTitle: string;
  initialWhen?: PublishWhen;
  initialDatetimeLocal?: string;
  onSent?: (target: PublishDialogTarget) => void;
}

/** Routes TikTok publishing to Direct Post when the linked account has
 *  `video.publish`, otherwise keeps the drafts flow. Calendar Later stays on
 *  drafts because scheduled Direct Post is not wired on the backend yet. */
export const TikTokPublishDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  clipId,
  clipTitle,
  initialWhen,
  initialDatetimeLocal,
  onSent,
}) => {
  const { t } = useTranslation();
  const { data, loading } = useQuery(MY_SOCIAL_CONNECTIONS, { skip: !open });
  const connectionsLoaded = Boolean(data) || !loading;
  const canDirectPost = useMemo(
    () => ((data?.mySocialConnections || []) as PublishConnection[])
      .some(isDirectPostConnection),
    [data],
  );
  const useDirectPost = connectionsLoaded && canDirectPost && initialWhen !== 'later';

  if (open && !connectionsLoaded) {
    return (
      <Dialog open onOpenChange={(next) => !next && onOpenChange(false)}>
        <DialogContent data-testid="tiktok-publish-loading">
          <DialogHeader>
            <DialogTitle>{t('publishing.tiktok.dialogTitle')}</DialogTitle>
            <DialogDescription>{t('publishing.tiktok.dialogDescription')}</DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            {t('publishing.tiktok.loadingCreator')}
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  if (useDirectPost) {
    return (
      <PublishToTikTokDialog
        open={open}
        onOpenChange={onOpenChange}
        clipId={clipId}
        clipTitle={clipTitle}
        onPublished={onSent}
      />
    );
  }

  return (
    <SendToTikTokDialog
      open={open}
      onOpenChange={onOpenChange}
      clipId={clipId}
      clipTitle={clipTitle}
      initialWhen={initialWhen}
      initialDatetimeLocal={initialDatetimeLocal}
      onSent={onSent}
    />
  );
};

export default TikTokPublishDialog;
